/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { z } from 'zod'

import { useTypedLocalStorage } from '@modules/utils/useTypedLocalStorage'
import useGetBudgets from '@modules/billing-lib/queries/useGetBudgets'
import { useConfig } from '@modules/cui/ConfigContext'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isTrial } from '@modules/billing-lib/billingDetails/utils'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

const SHOW_BUDGETS_TOUR = 'SHOW_BUDGETS_TOUR'
const TOTAL_STEPS = 2

const useBudgetTour = (): {
  showTour: boolean
  closeTour: () => void
  totalSteps: number
} => {
  const isUserConsole = useConfig('APP_NAME') === 'userconsole'
  const { data: billingData } = useBillingDetails()
  const isTrialOrg = billingData && isTrial(billingData)
  const organizationId = useOrganizationId()
  const shouldConsiderDisplayingTour = isUserConsole && !isTrialOrg
  const [showTour, setShowTour] = useTypedLocalStorage(SHOW_BUDGETS_TOUR, z.boolean(), true)
  const { data: budgets } = useGetBudgets(organizationId, {
    enabled: shouldConsiderDisplayingTour && showTour && Boolean(organizationId),
  })

  let _showTour = showTour

  // If budgets is undefined, we are likely still loading, or user has no permissions to access budgets
  // If user has created a budget (budgets.length), we don't need to show the tour anymore
  if (!shouldConsiderDisplayingTour || !budgets || budgets.length > 0) {
    _showTour = false
  }

  return {
    showTour: _showTour,
    closeTour: () => setShowTour(false),
    totalSteps: TOTAL_STEPS,
  }
}

export default useBudgetTour
